// src/frontend/js/modern/ui/tooltip-handler.js

export class TooltipHandler {
	constructor() {
		this._tooltip = null;
		this._hideTimeout = null;
		this._isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
		this._boundEvents = [];
		this._direction = 'bottom';
		this._tType = 'default';
		this._text = '';
		this._target = null;
		// Safe distance from viewport edges
		this._safeMargin = 8;
	}

	init() {
		this._createTooltipElement();
		this._bindEvents();
	}

	_createTooltipElement() {
		const old = document.getElementById('wlfmc-tooltip');
		if (old) old.remove();
		this._tooltip = document.createElement('div');
		this._tooltip.id = 'wlfmc-tooltip';
		this._tooltip.className = 'tooltip__hidden';
		// Ensure tooltip is positioned relative to viewport for accurate calculations
		this._tooltip.style.position = 'absolute';
		this._tooltip.style.zIndex = '999999';
		document.body.appendChild(this._tooltip);
	}

	_bindEvents() {
		const self = this;

		// Helper to safely get closest element
		const getSafeTarget = (target) => {
			if (!target || !(target instanceof Element)) return null;
			return target;
		};

		const onMouseEnter = (e) => {
			const safeTarget = getSafeTarget(e.target);
			if (!safeTarget) return;

			const target = safeTarget.closest('.wlfmc-tooltip');
			if (!target) return;

			self._target = target;
			self._tType = target.dataset.tooltipType || 'default';
			self._text = target.dataset.tooltipText || '';
			if (!self._text) return;

			// Determine initial direction preference
			const cachedDir = target._wlfmcTooltipDirection;
			if (cachedDir) {
				self._direction = cachedDir;
			} else {
				// Extract direction from class name if exists, otherwise default
				const dirClass = Array.from(target.classList).find(c => c.startsWith('wlfmc-tooltip-'));
				self._direction = dirClass ? dirClass.split('-').pop() : 'bottom';
			}

			if (e.type === 'touchstart') e.preventDefault();
			if (self._hideTimeout) {
				clearTimeout(self._hideTimeout);
				self._hideTimeout = null;
			}
			self._positionAndShowTooltip(target);
		};

		const onMouseLeave = (e) => {
			const safeTarget = getSafeTarget(e.target);
			// For mouseleave, we might need relatedTarget logic, but keeping it simple for now
			// If target is invalid, just hide
			if (e.type === 'touchend' && self._isTouchDevice) {
				self._hideTimeout = setTimeout(() => self._hideTooltip(), 1000);
			} else if (e.type === 'mouseleave') {
				self._hideTooltip();
			}
		};

		const onClickOutside = (e) => {
			const safeTarget = getSafeTarget(e.target);
			if (!safeTarget) return;

			if (!safeTarget.closest('.wlfmc-tooltip') && safeTarget !== self._tooltip) {
				self._hideTooltip();
			}
		};

		document.addEventListener('mouseenter', onMouseEnter, true);
		document.addEventListener('mouseleave', onMouseLeave, true);

		if (this._isTouchDevice) {
			document.addEventListener('touchstart', onMouseEnter, { passive: false });
			document.addEventListener('touchend', onMouseLeave, { passive: false });
		}

		document.addEventListener('touchstart', onClickOutside);
		document.addEventListener('click', onClickOutside);

		this._boundEvents = [
			{ event: 'mouseenter', handler: onMouseEnter, options: true },
			{ event: 'mouseleave', handler: onMouseLeave, options: true },
			{ event: 'touchstart', handler: onMouseEnter, options: { passive: false } },
			{ event: 'touchend', handler: onMouseLeave, options: { passive: false } },
			{ event: 'touchstart', handler: onClickOutside, options: undefined },
			{ event: 'click', handler: onClickOutside, options: undefined }
		];
	}

	/**
	 * Core positioning logic with intelligent fallback.
	 * Calculates available space and picks the best fitting position.
	 */
	_positionAndShowTooltip(target) {
		if (!this._tooltip || !target) return;
		// Prepare content and make visible but off-screen to measure dimensions
		this._tooltip.innerHTML = this._text;
		this._tooltip.className = `tooltip__expanded wlfmc-tooltip-${this._tType}`;
		this._tooltip.style.visibility = 'hidden';
		this._tooltip.style.display = 'block';
		this._tooltip.style.top = '-9999px';
		this._tooltip.style.left = '-9999px';

		// Force reflow to get accurate dimensions
		void this._tooltip.offsetWidth;

		const tipRect = this._tooltip.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();
		const winWidth = window.innerWidth;
		const winHeight = window.innerHeight;
		const gap = 7; // Distance between target and tooltip

		// Define all possible positions with their coordinates
		const positions = {
			top: {
				x: targetRect.left + (targetRect.width / 2) - (tipRect.width / 2),
				y: targetRect.top - tipRect.height - gap
			},
			bottom: {
				x: targetRect.left + (targetRect.width / 2) - (tipRect.width / 2),
				y: targetRect.bottom + gap
			},
			left: {
				x: targetRect.left - tipRect.width - gap,
				y: targetRect.top + (targetRect.height / 2) - (tipRect.height / 2)
			},
			right: {
				x: targetRect.right + gap,
				y: targetRect.top + (targetRect.height / 2) - (tipRect.height / 2)
			}
		};

		// Check if a position fits entirely within viewport
		const fits = (pos) => {
			return (
				pos.x >= this._safeMargin &&
				pos.x + tipRect.width <= winWidth - this._safeMargin &&
				pos.y >= this._safeMargin &&
				pos.y + tipRect.height <= winHeight - this._safeMargin
			);
		};

		let finalPos = null;
		let finalDir = this._direction;

		// 1. Try preferred direction first
		if (fits(positions[this._direction])) {
			finalPos = positions[this._direction];
		} else {
			// 2. Fallback: try other directions in order of available space
			const fallbackOrder = ['top', 'bottom', 'left', 'right'].filter(d => d !== this._direction);
			for (const dir of fallbackOrder) {
				if (fits(positions[dir])) {
					finalPos = positions[dir];
					finalDir = dir;
					break;
				}
			}

			// 3. Last resort: if nothing fits perfectly, pick the one with most visible area
			if (!finalPos) {
				let maxVisibleArea = -1;
				let bestFallbackDir = this._direction;
				Object.entries(positions).forEach(([dir, pos]) => {
					const visibleX = Math.min(pos.x + tipRect.width, winWidth) - Math.max(pos.x, 0);
					const visibleY = Math.min(pos.y + tipRect.height, winHeight) - Math.max(pos.y, 0);
					const area = visibleX * visibleY;
					if (area > maxVisibleArea) {
						maxVisibleArea = area;
						bestFallbackDir = dir;
					}
				});
				finalPos = positions[bestFallbackDir];
				finalDir = bestFallbackDir;
			}
		}

		// Clamp final coordinates to ensure at least partial visibility
		finalPos.x = Math.max(this._safeMargin, Math.min(finalPos.x, winWidth - tipRect.width - this._safeMargin));
		finalPos.y = Math.max(this._safeMargin, Math.min(finalPos.y, winHeight - tipRect.height - this._safeMargin));

		// Apply calculated position
		this._tooltip.style.left = `${finalPos.x + window.scrollX}px`;
		this._tooltip.style.top = `${finalPos.y + window.scrollY}px`;
		this._tooltip.style.visibility = 'visible';

		// Update direction class for arrow styling
		this._tooltip.classList.remove('tooltip__expanded-top', 'tooltip__expanded-bottom', 'tooltip__expanded-left', 'tooltip__expanded-right');
		this._tooltip.classList.add(`tooltip__expanded-${finalDir}`);

		// Cache successful direction on target element
		target._wlfmcTooltipDirection = finalDir;
	}

	_showTooltip() {
		// Kept for backward compatibility but logic moved to _positionAndShowTooltip
		if (this._target) {
			this._positionAndShowTooltip(this._target);
		}
	}

	_hideTooltip() {
		if (!this._tooltip) return;
		if (this._hideTimeout) {
			clearTimeout(this._hideTimeout);
			this._hideTimeout = null;
		}
		this._tooltip.style.top = '0';
		this._tooltip.style.left = '0';
		this._tooltip.className = 'tooltip__hidden';
		this._tooltip.style.display = '';
		this._tooltip.style.visibility = '';
	}

	destroy() {
		this._boundEvents.forEach(({ event, handler, options }) => {
			document.removeEventListener(event, handler, options);
		});
		this._boundEvents = [];
		if (this._tooltip) {
			this._tooltip.remove();
			this._tooltip = null;
		}
	}
}
